import { useState, useEffect } from 'react';
import { dashboardAPI, seedAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Package, Users, Megaphone, Handshake, DollarSign, PauseCircle, CheckCircle } from 'lucide-react';

export default function AdminDashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const load = async () => {
      try { await seedAPI.seed(); } catch (e) { console.error('Seed error:', e); }
      try { const r = await dashboardAPI.stats(); setStats(r.data); } catch (e) { console.error(e); }
    };
    load();
  }, []);

  const adminCards = stats ? [
    { label: 'Available Units', value: stats.inventory_available, icon: Package, color: 'text-green-500' },
    { label: 'On Hold', value: stats.inventory_hold, icon: PauseCircle, color: 'text-amber-500' },
    { label: 'Sold', value: stats.inventory_sold, icon: CheckCircle, color: 'text-blue-500' },
    { label: 'New Leads', value: stats.new_leads, icon: Megaphone, color: 'text-[#E31937]' },
    { label: 'Active Deals', value: stats.active_deals, icon: Handshake, color: 'text-purple-500' },
    { label: 'Pending Financing', value: stats.pending_financing, icon: DollarSign, color: 'text-orange-500' },
    { label: 'Total Clients', value: stats.total_clients, icon: Users, color: 'text-cyan-500' },
  ] : [];

  const staffCards = stats ? [
    { label: 'Available Units', value: stats.inventory_available, icon: Package, color: 'text-green-500' },
    { label: 'My Leads', value: stats.my_leads, icon: Megaphone, color: 'text-[#E31937]' },
    { label: 'My Clients', value: stats.my_clients, icon: Users, color: 'text-cyan-500' },
    { label: 'My Deals', value: stats.my_deals, icon: Handshake, color: 'text-purple-500' },
  ] : [];

  const cards = isAdmin ? adminCards : staffCards;

  return (
    <div data-testid="admin-dashboard">
      <h1 className="text-2xl font-bold text-gray-900 uppercase mb-6">Dashboard</h1>

      {!stats ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(isAdmin ? 7 : 4)].map((_, i) => <div key={i} className="animate-pulse bg-gray-100 h-28 rounded-lg" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map(c => (
            <Card key={c.label} className="bg-white border-gray-200 text-gray-900">
              <CardContent className="p-5 flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">{c.label}</p>
                  <p className="text-3xl font-bold mt-1">{c.value}</p>
                </div>
                <c.icon className={`w-8 h-8 ${c.color} opacity-60`} />
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
